import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useRef, useCallback } from 'react';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CircularProgress, CssBaseline, Toolbar, useMediaQuery, Button } from '@mui/material';

// project imports
import Breadcrumbs from 'components/ui-component/extended/Breadcrumbs';
import { Header, Sidebar } from 'components/layout/main-layout/sections';
import ReduxModal from 'components/ui-component/modal/ReduxModal';
import ProgressModal from 'components/ui-component/modal/ProgressModal';
import NoticePopupModal from 'components/modals/NoticePopupModal';

import { PERMISSIONS, drawerWidth } from 'store/baseConfig';
import { menuOpen, setUserMenu, setIsMobile, selectSidebarOpened, selectShowUserMenu, selectIsMobile } from 'store/slice/SliceSidebar';
import { closeModal, openModal } from 'store/slice/SliceModal';
import menus from 'menus';
import { fetchNoticePopups } from 'apis/Service';
import { getReadNoticeIds, isDontShowToday, isNoticeCombinationChanged, setLastSeenNoticeCombination } from 'utils/utilsNoticeStorage';

// assets
import { IconChevronRight } from '@tabler/icons-react';
import MainCard from 'components/ui-component/cards/MainCard';
import { Outlet, useLocation } from 'react-router-dom';
import ErrorBoundary from 'components/layout/ErrorBoundary';
import { Suspense } from 'react';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const location = useLocation();
    
    // Handle left drawer
    const leftDrawerOpened = useSelector(selectSidebarOpened);
    const handleLeftDrawerToggle = () => {
        dispatch(menuOpen(!leftDrawerOpened));
    };

    const { loginState, loginUserInfo } = useSelector((state) => state.auth);
    const { isOpen, modalType, modalProps } = useSelector((state) => state.modal);

    // 리덕스에서 상태 가져오기
    const showUserMenu = useSelector(selectShowUserMenu);
    const isMobile = useSelector(selectIsMobile);

    const userInfo = loginUserInfo.user;
    let perm = null; // 권한 id (관리자:01, 운영자:11, 사용자:21)
    if (userInfo) {
        perm = userInfo.perm_id;
    }

    // 공지사항 팝업 관련 상태
    const [unreadNotices, setUnreadNotices] = useState([]);
    const [isNoticePopupOpen, setIsNoticePopupOpen] = useState(false);
    const noticeCheckIntervalRef = useRef(null);
    const hasCheckedOnLoginRef = useRef(false);

    // 모바일 상태 감지 및 저장
    useEffect(() => {
        dispatch(setIsMobile(matchDownMd));
    }, [matchDownMd, dispatch]);

    // 모바일 접속 시 관리자는 자동으로 사용자 메뉴로 전환
    useEffect(() => {
        if (isMobile && perm === PERMISSIONS.ADMIN) {
            dispatch(setUserMenu(true));
        }
    }, [isMobile, perm, dispatch]);

    // 공지사항 대상자 체크 함수
    const checkNoticeTarget = (notice, userPerm) => {
        if (!notice.target) return false;
        
        if (notice.target === 'all') return true;
        if (notice.target === 'admin' && (userPerm === PERMISSIONS.SUPER_ADMIN || userPerm === PERMISSIONS.ADMIN)) return true;
        if (notice.target === 'employee' && userPerm === PERMISSIONS.USER) return true;
        
        return false;
    };

    // 공지사항 날짜 범위 체크 함수
    const isNoticeInDateRange = (notice) => {
        if (!notice.webPopupStartDtime || !notice.webPopupEndDtime) return false;
        
        const now = new Date();
        const startDate = new Date(notice.webPopupStartDtime);
        const endDate = new Date(notice.webPopupEndDtime);
        
        // 종료일은 하루 종료 시각(23:59:59)까지 포함
        endDate.setHours(23, 59, 59, 999);
        
        return now >= startDate && now <= endDate;
    };

    // 읽지 않은 공지사항 조회 및 필터링
    const checkUnreadNotices = useCallback(async () => {
        if (!loginState || !userInfo || !perm) return;

        // 상세보기 페이지에서는 팝업을 표시하지 않음
        if (location.pathname.includes('/board/notice/detail/')) {
            console.log('상세보기 페이지에서는 팝업을 표시하지 않음');
            return;
        }

        try {
            const response = await fetchNoticePopups();
            console.log('공지사항 팝업 API 응답:', response);
            
            const notices = response?.data || response || [];
            console.log('공지사항 목록:', notices);
            
            if (!Array.isArray(notices)) {
                console.log('공지사항 목록이 배열이 아님:', notices);
                return;
            }

            // 오늘 하루 안보기 체크
            if (isDontShowToday()) {
                console.log('오늘 하루 안보기 설정되어 있음');
                return;
            }

            const readNoticeIds = getReadNoticeIds();
            console.log('읽은 공지사항 ID 목록:', readNoticeIds);
            
            const currentIds = notices.map(notice => notice.no || notice.NO).filter(Boolean);
            const isChanged = isNoticeCombinationChanged(currentIds);
            console.log('공지사항 조합 변경 여부:', isChanged);

            // 읽지 않은 공지사항이 있거나 조합이 변경된 경우 표시
            const hasUnread = notices.some(notice => !readNoticeIds.includes(notice.no || notice.NO));

            if (isChanged || hasUnread) {
                console.log('팝업 표시 (조합 변경 또는 읽지 않음):', notices);
                setUnreadNotices(notices);
                setIsNoticePopupOpen(true);
            } else {
                console.log('표시할 공지사항이 없음 (이미 확인한 조합)');
            }
        } catch (error) {
            console.error('공지사항 조회 실패:', error);
        }
    }, [loginState, userInfo, perm, location.pathname]);

    // 로그인 완료 시 공지사항 체크 (읽음 여부 무시)
    const checkNoticesOnLogin = useCallback(async () => {
        if (!loginState || !userInfo || !perm) return;

        const forceShow = sessionStorage.getItem('forceNoticeShow') === 'true';

        // 이번 세션에서 이미 체크했거나 팝업이 표시되었다면 스킵
        // 단, forceShow가 true인 경우는 로그인 직후이므로 무조건 진행
        if (!forceShow && (hasCheckedOnLoginRef.current)) {
            return;
        }

        // 상세보기 페이지에서는 팝업을 표시하지 않음
        if (location.pathname.includes('/board/notice/detail/')) {
            console.log('상세보기 페이지에서는 팝업을 표시하지 않음');
            return;
        }

        // 오늘 하루 안보기 체크
        if (isDontShowToday()) {
            console.log('오늘 하루 안보기 설정되어 있음');
            // forceShow라도 오늘 하루 안보기가 설정되어 있으면 플래그만 지우고 종료
            sessionStorage.removeItem('forceNoticeShow');
            hasCheckedOnLoginRef.current = true;
            return;
        }

        try {
            const response = await fetchNoticePopups();
            const notices = response?.data || response || [];
            
            if (!Array.isArray(notices) || notices.length === 0) {
                sessionStorage.removeItem('forceNoticeShow');
                hasCheckedOnLoginRef.current = true;
                return;
            }

            const currentIds = notices.map(notice => notice.no || notice.NO).filter(Boolean);
            const isChanged = isNoticeCombinationChanged(currentIds);

            // 로그인 직후(forceShow)거나 조합이 변경된 경우 표시
            if (forceShow || isChanged) {
                console.log(forceShow ? '로그인 직후 강제 표시' : '조합 변경으로 인한 표시');
                setUnreadNotices(notices);
                setIsNoticePopupOpen(true);
                
                // 표시했으므로 조합 저장
                setLastSeenNoticeCombination(currentIds);
            }

            // 체크 완료 상태 기록 및 플래그 제거
            hasCheckedOnLoginRef.current = true;
            sessionStorage.removeItem('forceNoticeShow');

        } catch (error) {
            console.error('공지사항 조회 실패:', error);
        }
    }, [loginState, userInfo, perm, location.pathname]);

    // 로그인 완료 시 공지사항 체크
    useEffect(() => {
        if (loginState && userInfo && perm) {
            // 로그인 완료 시 즉시 체크 (읽음 여부 무시)
            checkNoticesOnLogin();
        } else {
            // 로그아웃 시 상태 초기화
            hasCheckedOnLoginRef.current = false;
        }
    }, [loginState, userInfo, perm, checkNoticesOnLogin]);

    // 10분마다 공지사항 체크 (로그인 상태일 때만)
    useEffect(() => {
        if (!loginState || !userInfo) {
            // 로그아웃 시 인터벌 클리어
            if (noticeCheckIntervalRef.current) {
                clearInterval(noticeCheckIntervalRef.current);
                noticeCheckIntervalRef.current = null;
            }
            return;
        }

        // 1시간(3600000ms)마다 체크
        noticeCheckIntervalRef.current = setInterval(() => {
            checkUnreadNotices();
        }, 60 * 60 * 1000);

        // cleanup
        return () => {
            if (noticeCheckIntervalRef.current) {
                clearInterval(noticeCheckIntervalRef.current);
                noticeCheckIntervalRef.current = null;
            }
        };
    }, [loginState, userInfo, checkUnreadNotices]);

    const handleCloseModal = () => {
        dispatch(closeModal());
    };

    // Progress modal context for legacy support
    const progressContext = {
        openProgressModal: () => dispatch(openModal({ modalType: 'progress' })),
        closeProgressModal: handleCloseModal
    };

    let menuList;

    switch (perm) {
        case PERMISSIONS.SUPER_ADMIN:
            menuList = menus[PERMISSIONS.SUPER_ADMIN];
            break;
        case PERMISSIONS.ADMIN:
            menuList = menus[PERMISSIONS.ADMIN];
            break;
        case PERMISSIONS.USER:
            menuList = menus[PERMISSIONS.USER];
            break;
        default:
            menuList = menus[PERMISSIONS.ALL];
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    transition: leftDrawerOpened ? theme.transitions.create('width') : 'none'
                }}
            >
                <Toolbar>
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                </Toolbar>
            </AppBar>

            {/* drawer */}
            <Sidebar 
                drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened} 
                drawerToggle={handleLeftDrawerToggle} 
                menuList={menuList}
                isAdmin={perm === PERMISSIONS.ADMIN}
            />

            {/* main content */}
            <ErrorBoundary>
                <Suspense fallback={<CircularProgress></CircularProgress>}>
                    <Main open={leftDrawerOpened}>
                        {/* breadcrumb */}
                        <Breadcrumbs separator={IconChevronRight} navigation={{ items: menuList }} icon title rightAlign />
                        <MainCard>
                            <Outlet context={progressContext} />
                        </MainCard>
                    </Main>
                </Suspense>
            </ErrorBoundary>

            {/* Global Modals */}
            <ReduxModal 
                open={isOpen && modalType === 'basic'} 
                handleClose={handleCloseModal}
                title={modalProps?.title || ''}
                message={modalProps?.message || ''}
                severity={modalProps?.severity || 'info'}
            />
            <ProgressModal 
                open={isOpen && modalType === 'progress'} 
                handleClose={handleCloseModal}
            />

            {/* 공지사항 팝업 모달 - 상세보기 페이지에서는 표시하지 않음 */}
            {!location.pathname.includes('/board/notice/detail/') && (
                <NoticePopupModal
                    open={isNoticePopupOpen}
                    onClose={() => {
                        setIsNoticePopupOpen(false);
                        setUnreadNotices([]);
                    }}
                    notices={unreadNotices}
                />
            )}

            {/* <Customization /> */}
        </Box>
    );
};

export default MainLayout;
